import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import client from "@/lib/api";
import { SectionHeader, EmptyState, notifyError, notifyOk } from "./_ui";
import SpecsEditor from "./SpecsEditor";
import { getDecoderRefs, REF_FIELDS } from "@/lib/decoderRefs";
import { isValidVin, VIN_HINT } from "@/lib/vin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = {
  vin: "", brand: "", model: "", modification: "", fuel_type: "",
  engine_type: "", engine_volume: "", cylinders: "", power_hp: "", power_kw: "",
  drive: "", body_style: "", year_from: "", year_to: "", specs: [], source: "manual",
};

const FIELDS = [
  ["brand", "Марка"], ["model", "Модель"], ["modification", "Модификация"],
  ["fuel_type", "Тип топлива"], ["engine_type", "Тип двигателя"],
  ["engine_volume", "Объём двигателя (л)"], ["cylinders", "Цилиндры"],
  ["power_hp", "Мощность (л.с.)"], ["power_kw", "Мощность (кВт)"],
  ["drive", "Привод"], ["body_style", "Тип кузова"],
  ["year_from", "Год выпуска (с)"], ["year_to", "Год выпуска (по)"],
];

const yearRange = (r) => {
  const f = r.year_from || r.year, tt = r.year_to;
  if (f && tt && tt !== f) return `${f}–${tt}`;
  return f || "";
};

export default function VinRecords() {
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [refs, setRefs] = useState({});

  const load = (query = "") =>
    client.get("/admin/vin-records", { params: { q: query } }).then((r) => setRecords(r.data)).catch(notifyError);
  useEffect(() => { load(); getDecoderRefs().then(setRefs); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...empty, ...r, specs: r.specs || [] }); setEditing(r.id); setOpen(true); };

  const save = async () => {
    if (!isValidVin(form.vin)) { notifyError({ message: "Неверный формат VIN. " + VIN_HINT }); return; }
    try {
      if (editing) await client.put(`/admin/vin-records/${editing}`, form);
      else await client.post("/admin/vin-records", form);
      setOpen(false); notifyOk("Сохранено"); load(q);
    } catch (e) { notifyError(e); }
  };
  const remove = async (id) => {
    if (!window.confirm("Удалить запись?")) return;
    await client.delete(`/admin/vin-records/${id}`); load(q);
  };

  return (
    <div>
      <SectionHeader
        title="База VIN (декодер)"
        description="Записи для VIN-декодера. Пополняются вручную и автоматически из выпущенных актов."
        action={
          <Button data-testid="vin-add-btn" onClick={openNew} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
            <Plus className="w-4 h-4 mr-1.5" /> Запись
          </Button>
        }
      />

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input data-testid="vin-search" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Поиск по VIN" className="pl-9 border-neutral-300" />
      </div>

      {records.length === 0 ? (
        <EmptyState text="Записей пока нет." />
      ) : (
        <div className="border border-neutral-200 rounded-lg bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>VIN</TableHead>
                <TableHead>Марка</TableHead>
                <TableHead>Модель</TableHead>
                <TableHead>Годы</TableHead>
                <TableHead>Источник</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id} data-testid={`vin-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.vin}</TableCell>
                  <TableCell>{r.brand}</TableCell>
                  <TableCell>{r.model}</TableCell>
                  <TableCell>{yearRange(r)}</TableCell>
                  <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100">{r.source}</span></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => openEdit(r)} className="inline-flex p-1.5 text-neutral-600 hover:text-black" data-testid={`vin-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(r.id)} className="inline-flex p-1.5 text-red-600" data-testid={`vin-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Редактировать запись" : "Новая запись VIN"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>VIN</Label>
              <Input data-testid="vin-input" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className={`border-neutral-300 font-mono ${form.vin && !isValidVin(form.vin) ? "border-red-500 focus-visible:ring-red-500" : ""}`} />
              {form.vin && !isValidVin(form.vin) && (
                <p className="text-xs text-red-600" data-testid="vin-input-error">{VIN_HINT}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{label}</Label>
                  {REF_FIELDS.includes(k) ? (
                    <Select value={form[k] || "__none"} onValueChange={(v) => setForm({ ...form, [k]: v === "__none" ? "" : v })}>
                      <SelectTrigger data-testid={`vin-${k}`} className="border-neutral-300"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">—</SelectItem>
                        {(refs[k] || []).map((o) => (
                          <SelectItem key={o.code} value={o.code}>{o.label?.ru || o.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid={`vin-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="border-neutral-300" />
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Дополнительные характеристики</Label>
              <SpecsEditor specs={form.specs} onChange={(s) => setForm({ ...form, specs: s })} testid="vin-specs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Отмена</Button>
            <Button data-testid="vin-save-btn" onClick={save} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
